import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button, Modal, Form, Spinner, Alert, Tab, Tabs } from 'react-bootstrap';
import Navbarpro from './sidebar';
import { api } from '../../../config';

function VendorDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [vendor, setVendor] = useState(null);
    const [performance, setPerformance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showKYCModal, setShowKYCModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [statusForm, setStatusForm] = useState({ status: '', reason: '' });
    const [kycForm, setKycForm] = useState({ kycStatus: '', rejectionReason: '' });

    useEffect(() => {
        fetchVendorDetails();
    }, [id]);

    const fetchVendorDetails = async () => {
        setLoading(true);
        try {
            // Try new admin endpoint first
            let vendorData;
            try {
                const res = await api.get(`/admin/vendors/${id}`);
                vendorData = res.data;
            } catch {
                // Fallback to legacy endpoint
                const res = await api.get(`/api/getdetails/vendor/${id}`);
                vendorData = { vendor: res.data, performance: {} };
            }

            setVendor(vendorData.vendor);
            setPerformance(vendorData.performance || {});
        } catch (err) {
            console.error('Error:', err);
            setError('Failed to load vendor details');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async () => {
        setActionLoading(true);
        try {
            await api.put(`/admin/vendors/${id}/status`, statusForm);
            setSuccess('Vendor status updated successfully');
            setShowStatusModal(false);
            fetchVendorDetails();
        } catch (err) {
            setError('Failed to update status');
        } finally {
            setActionLoading(false);
        }
    };

    const handleKYCUpdate = async () => {
        setActionLoading(true);
        try {
            await api.put(`/admin/vendors/${id}/kyc`, kycForm);
            setSuccess('KYC status updated successfully');
            setShowKYCModal(false);
            fetchVendorDetails();
        } catch (err) {
            setError('Failed to update KYC status');
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            active: { bg: 'success', icon: 'check-circle' },
            suspended: { bg: 'warning', icon: 'pause-circle' },
            banned: { bg: 'danger', icon: 'x-circle' }
        };
        const s = styles[status] || styles.active;
        return (
            <Badge bg={s.bg} className="d-inline-flex align-items-center gap-1">
                <i className={`bi bi-${s.icon}`}></i> {status || 'Active'}
            </Badge>
        );
    };

    const getKYCBadge = (status) => {
        const styles = {
            pending: { class: 'kyc-pending', icon: 'hourglass-split' },
            verified: { class: 'kyc-verified', icon: 'patch-check' },
            rejected: { class: 'kyc-rejected', icon: 'x-circle' }
        };
        const s = styles[status] || styles.pending;
        return (
            <span className={s.class}>
                <i className={`bi bi-${s.icon} me-1`}></i> {status || 'Pending'}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="loading-state">
                <Navbarpro />
                <div className="text-center" style={{ marginTop: '20vh' }}>
                    <Spinner animation="border" />
                    <p className="mt-3">Loading vendor details...</p>
                </div>
            </div>
        );
    }

    if (!vendor) {
        return (
            <div>
                <Navbarpro />
                <Container className="py-5 text-center">
                    <i className="bi bi-person-x fs-1 text-muted"></i>
                    <h5 className="mt-3">Vendor not found</h5>
                    <Button variant="outline-primary" onClick={() => navigate('/vendors')}>
                        <i className="bi bi-arrow-left me-2"></i>Back to Vendors
                    </Button>
                </Container>
            </div>
        );
    }

    return (
        <div>
            <Navbarpro />

            <Container fluid className="py-4 px-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-3">
                        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/vendors')}>
                            <i className="bi bi-arrow-left"></i>
                        </Button>
                        <div>
                            <h4 className="mb-0">Vendor Details</h4>
                            <small className="text-secondary">ID: {id}</small>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-info" size="sm" onClick={() => {
                            setStatusForm({ status: vendor.status || 'active', reason: '' });
                            setShowStatusModal(true);
                        }}>
                            <i className="bi bi-sliders me-1"></i> Change Status
                        </Button>
                        <Button variant="outline-primary" size="sm" onClick={() => {
                            setKycForm({ kycStatus: vendor.kycStatus || 'pending', rejectionReason: '' });
                            setShowKYCModal(true);
                        }}>
                            <i className="bi bi-patch-check me-1"></i> Update KYC
                        </Button>
                    </div>
                </div>

                {/* Alerts */}
                {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
                {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

                <Row className="g-4">
                    {/* Profile Card */}
                    <Col lg={4}>
                        <Card className="h-100">
                            <Card.Body className="text-center">
                                <div className="vendor-avatar mx-auto mb-3">
                                    {vendor.Business_Name?.charAt(0) || 'V'}
                                </div>
                                <h5 className="mb-1">{vendor.Business_Name || 'Unknown Business'}</h5>
                                <p className="text-secondary mb-3">{vendor.Owner_name || 'Unknown Owner'}</p>

                                <div className="d-flex justify-content-center gap-2 mb-3">
                                    {getStatusBadge(vendor.status)}
                                    {getKYCBadge(vendor.kycStatus)}
                                </div>

                                <Badge bg={vendor.Category === 'Technical' ? 'primary' : vendor.Category === 'Non-Technical' ? 'success' : 'info'} className="mb-3">
                                    {vendor.Category || 'Unknown Category'}
                                </Badge>

                                <hr />

                                <div className="text-start">
                                    <p className="mb-2">
                                        <i className="bi bi-envelope me-2 text-primary"></i>
                                        {vendor.Email_address || 'N/A'}
                                    </p>
                                    <p className="mb-2">
                                        <i className="bi bi-phone me-2 text-primary"></i>
                                        {vendor.Phone_number || 'N/A'}
                                    </p>
                                    <p className="mb-2">
                                        <i className="bi bi-geo-alt me-2 text-primary"></i>
                                        {vendor.Business_address || 'N/A'}
                                    </p>
                                    <p className="mb-0">
                                        <i className="bi bi-tag me-2 text-primary"></i>
                                        {Array.isArray(vendor.Sub_Category) ? vendor.Sub_Category.join(', ') : vendor.Sub_Category || 'N/A'}
                                    </p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Performance Metrics */}
                    <Col lg={8}>
                        <Card className="mb-4">
                            <Card.Body>
                                <h6 className="mb-4">
                                    <i className="bi bi-graph-up me-2" style={{ color: 'var(--primary)' }}></i>
                                    Performance Metrics
                                </h6>
                                <Row className="g-3">
                                    <Col md={3}>
                                        <div className="metric-card">
                                            <div className="metric-value">{performance?.completedJobs || 0}</div>
                                            <div className="metric-label">Jobs Completed</div>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="metric-card">
                                            <div className="metric-value">{performance?.totalOrders || 0}</div>
                                            <div className="metric-label">Total Orders</div>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="metric-card">
                                            <div className="metric-value" style={{ color: 'var(--accent-yellow)' }}>
                                                {performance?.avgRating || 0}
                                                <i className="bi bi-star-fill ms-1" style={{ fontSize: '0.75em' }}></i>
                                            </div>
                                            <div className="metric-label">Avg Rating</div>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="metric-card">
                                            <div className="metric-value" style={{ color: 'var(--accent-red)' }}>
                                                {performance?.complaintsCount || 0}
                                            </div>
                                            <div className="metric-label">Complaints</div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* KYC Documents */}
                        <Card>
                            <Card.Body>
                                <h6 className="mb-4">
                                    <i className="bi bi-file-earmark-text me-2" style={{ color: 'var(--accent-cyan)' }}></i>
                                    KYC Documents
                                </h6>
                                <Tabs defaultActiveKey="overview" className="mb-3">
                                    <Tab eventKey="overview" title="Overview">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <div className="p-3 rounded" style={{ background: 'var(--bg-secondary)' }}>
                                                    <strong>Tax ID / GST</strong>
                                                    <p className="mb-0 text-secondary">{vendor.Tax_ID || 'Not provided'}</p>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="p-3 rounded" style={{ background: 'var(--bg-secondary)' }}>
                                                    <strong>Location</strong>
                                                    <p className="mb-0 text-secondary">
                                                        {vendor.Latitude && vendor.Longitude
                                                            ? `${vendor.Latitude}, ${vendor.Longitude}`
                                                            : 'Not provided'}
                                                    </p>
                                                </div>
                                            </Col>
                                            {vendor.ProductUrl && (
                                                <Col md={12}>
                                                    <div className="p-3 rounded" style={{ background: 'var(--bg-secondary)' }}>
                                                        <strong>Uploaded Document</strong>
                                                        <div className="mt-2">
                                                            <a href={vendor.ProductUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                                                <i className="bi bi-file-earmark-pdf me-2"></i>View Document
                                                            </a>
                                                        </div>
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>
                                    </Tab>
                                    <Tab eventKey="aadhar" title="Aadhar">
                                        <div className="text-center py-4 text-secondary">
                                            {vendor.kycDocuments?.aadhar
                                                ? <img src={vendor.kycDocuments.aadhar} alt="Aadhar" className="img-fluid" style={{ maxHeight: '300px' }} />
                                                : 'Aadhar document not uploaded'
                                            }
                                        </div>
                                    </Tab>
                                    <Tab eventKey="pan" title="PAN">
                                        <div className="text-center py-4 text-secondary">
                                            {vendor.kycDocuments?.pan
                                                ? <img src={vendor.kycDocuments.pan} alt="PAN" className="img-fluid" style={{ maxHeight: '300px' }} />
                                                : 'PAN document not uploaded'
                                            }
                                        </div>
                                    </Tab>
                                </Tabs>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Status Modal */}
            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton style={{ background: 'var(--bg-secondary)' }}>
                    <Modal.Title>Update Vendor Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                            value={statusForm.status}
                            onChange={(e) => setStatusForm(prev => ({ ...prev, status: e.target.value }))}
                        >
                            <option value="active">Active</option>
                            <option value="suspended">Suspended</option>
                            <option value="banned">Banned</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Reason (optional)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={statusForm.reason}
                            onChange={(e) => setStatusForm(prev => ({ ...prev, reason: e.target.value }))}
                            placeholder="Provide reason for status change..."
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStatusModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleStatusUpdate} disabled={actionLoading}>
                        {actionLoading ? <Spinner size="sm" /> : 'Update Status'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* KYC Modal */}
            <Modal show={showKYCModal} onHide={() => setShowKYCModal(false)} centered>
                <Modal.Header closeButton style={{ background: 'var(--bg-secondary)' }}>
                    <Modal.Title>Update KYC Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>KYC Status</Form.Label>
                        <Form.Select
                            value={kycForm.kycStatus}
                            onChange={(e) => setKycForm(prev => ({ ...prev, kycStatus: e.target.value }))}
                        >
                            <option value="pending">Pending</option>
                            <option value="verified">Verified</option>
                            <option value="rejected">Rejected</option>
                        </Form.Select>
                    </Form.Group>
                    {kycForm.kycStatus === 'rejected' && (
                        <Form.Group>
                            <Form.Label>Rejection Reason</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={kycForm.rejectionReason}
                                onChange={(e) => setKycForm(prev => ({ ...prev, rejectionReason: e.target.value }))}
                                placeholder="Provide reason for rejection..."
                            />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowKYCModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleKYCUpdate} disabled={actionLoading}>
                        {actionLoading ? <Spinner size="sm" /> : 'Update KYC'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default VendorDetails;
